import { api } from '@/api/core';
import type { GetActiveUsersForOrgResponse } from '@/api/users/getActiveUsersForOrg';
import type { TeamsListResponse } from '@/api/teams/teamsList';
import type { UserAboutData } from '@/api/users/userAbout';
import type { CreateProjectRequest, CreateProjectResponse } from './createProject';
import type { GetProjectFilterOptionsResponse } from './getProjectFilterOptions';
import type { GetProjectsRequest, GetProjectsResponse, ProjectData } from './getProjects';
import type { UpdateProjectRequest } from './updateProject';
import { replaceMockItem } from './mockUtils';

type MockProjectSeed = Omit<ProjectData, 'projectId' | 'ownerId'>;

const BASE_PROJECTS: MockProjectSeed[] = [
  {
    name: 'Q2 Marketing Strategy',
    description: 'Campaign planning and budget',
    owner: 'Sam Dale',
    team: 'Marketing',
    visibility: 'Team',
    sharedWith: ['Jo Smith', 'Alex Jones', 'Helen Dean'],
    numberOfFiles: 14,
    lastModifiedDatetime: '2026-05-28T14:30:00Z',
  },
  {
    name: 'Office Relocation Planning',
    description: 'Floor plans and vendor quotes',
    owner: 'Mr Steven Bond',
    team: 'Operations',
    visibility: 'All',
    sharedWith: [],
    numberOfFiles: 14,
    lastModifiedDatetime: '2026-05-27T09:15:00Z',
  },
  {
    name: 'Training Materials Review',
    description: null,
    owner: 'Jo Smith',
    team: 'Legal',
    visibility: 'Private',
    sharedWith: ['Sam Dale'],
    numberOfFiles: 3,
    lastModifiedDatetime: '2026-05-25T16:45:00Z',
  },
  {
    name: 'Competitor Analysis 2026',
    description: 'Market positioning research',
    owner: 'Sam Dale',
    team: 'Marketing',
    visibility: 'Team',
    sharedWith: ['Helen Dean'],
    numberOfFiles: 6,
    lastModifiedDatetime: '2026-05-22T11:00:00Z',
  },
  {
    name: 'Brand Refresh Project',
    description: 'Visual identity overhaul',
    owner: 'Ms Helen Dean',
    team: 'Marketing',
    visibility: 'Team',
    sharedWith: ['Alex Jones', 'Jo Smith', 'Sam Dale'],
    numberOfFiles: 11,
    lastModifiedDatetime: '2026-05-20T13:20:00Z',
  },
  {
    name: 'Client Onboarding Docs',
    description: 'New client welcome pack',
    owner: 'Alex Jones',
    team: 'HR',
    visibility: 'Private',
    sharedWith: ['Sam Dale'],
    numberOfFiles: 5,
    lastModifiedDatetime: '2026-05-18T10:05:00Z',
  },
  {
    name: 'Annual Report Drafts',
    description: 'Financial year summaries',
    owner: 'Ms Helen Dean',
    team: 'Finance',
    visibility: 'Team',
    sharedWith: [],
    numberOfFiles: 9,
    lastModifiedDatetime: '2026-05-15T15:40:00Z',
  },
  {
    name: 'IT System Migration',
    description: 'Cloud migration docs',
    owner: 'Mr Steven Bond',
    team: 'IT',
    visibility: 'All',
    sharedWith: [],
    numberOfFiles: 22,
    lastModifiedDatetime: '2026-05-14T08:55:00Z',
  },
  {
    name: 'Employee Handbook Update',
    description: 'Policy updates for 2026',
    owner: 'Jo Smith',
    team: 'HR',
    visibility: 'Team',
    sharedWith: ['Sam Dale', 'Jo Smith'],
    numberOfFiles: 4,
    lastModifiedDatetime: '2026-05-12T17:10:00Z',
  },
  {
    name: 'Sustainability Audit',
    description: 'ESG compliance review',
    owner: 'Sam Dale',
    team: 'Compliance',
    visibility: 'Private',
    sharedWith: ['Ms Helen Dean'],
    numberOfFiles: 7,
    lastModifiedDatetime: '2026-05-10T12:25:00Z',
  },
  {
    name: 'Product Launch Plan',
    description: 'Go-to-market strategy',
    owner: 'Alex Jones',
    team: 'Product',
    visibility: 'Team',
    sharedWith: ['Jo Smith', 'Helen Dean', 'Sam Dale', 'Steven Bond'],
    numberOfFiles: 12,
    lastModifiedDatetime: '2026-05-08T09:35:00Z',
  },
  {
    name: 'Legal Templates',
    description: 'Standard contract templates',
    owner: 'Sam Dale',
    team: 'Legal',
    visibility: 'Private',
    sharedWith: [],
    numberOfFiles: 8,
    lastModifiedDatetime: '2026-05-06T14:50:00Z',
  },
  {
    name: 'Budget Forecasting',
    description: 'H2 financial projections',
    owner: 'Jo Smith',
    team: 'Finance',
    visibility: 'Team',
    sharedWith: ['Sam Dale'],
    numberOfFiles: 6,
    lastModifiedDatetime: '2026-05-04T11:15:00Z',
  },
  {
    name: 'Partner Agreements',
    description: 'Third-party agreements',
    owner: 'Mr Steven Bond',
    team: 'Legal',
    visibility: 'Team',
    sharedWith: ['Alex Jones'],
    numberOfFiles: 10,
    lastModifiedDatetime: '2026-05-02T16:00:00Z',
  },
  {
    name: 'Internal Comms Strategy',
    description: 'Staff newsletter content',
    owner: 'Sam Dale',
    team: 'Comms',
    visibility: 'All',
    sharedWith: [],
    numberOfFiles: 3,
    lastModifiedDatetime: '2026-04-30T10:45:00Z',
  },
  {
    name: 'Data Governance Review',
    description: 'GDPR compliance docs',
    owner: 'Jo Smith',
    team: 'Compliance',
    visibility: 'Private',
    sharedWith: ['Sam Dale', 'Jo Smith'],
    numberOfFiles: 15,
    lastModifiedDatetime: '2026-04-28T13:30:00Z',
  },
  {
    name: 'Q3 Sales Targets',
    description: 'Revenue targets by region',
    owner: 'Ms Helen Dean',
    team: 'Sales',
    visibility: 'Team',
    sharedWith: ['Mr Steven Bond'],
    numberOfFiles: 5,
    lastModifiedDatetime: '2026-04-25T09:00:00Z',
  },
];

const GENERATED_PROJECT_COUNT = 120;

const GENERATED_TOPICS = [
  'Vendor Contracts',
  'Quarterly Review',
  'Risk Assessment',
  'Website Redesign',
  'Customer Research',
  'Compliance Training',
  'Recruitment Drive',
  'Expense Policy',
  'Sales Playbook',
  'Facilities Upgrade',
] as const;

const GENERATED_DESCRIPTIONS = [
  'Working documents and notes',
  'Draft materials for review',
  null,
  'Reference files and templates',
  'Approved final versions',
  'Supporting research and data',
] as const;

const GENERATED_OWNERS = ['Sam Dale', 'Mr Steven Bond', 'Jo Smith', 'Ms Helen Dean', 'Alex Jones'] as const;

const GENERATED_TEAMS = [
  'Marketing',
  'Operations',
  'Legal',
  'HR',
  'Finance',
  'IT',
  'Compliance',
  'Product',
  'Comms',
  'Sales',
] as const;

const GENERATED_VISIBILITIES = ['Team', 'All', 'Private'] as const;

const GENERATED_SHARED_WITH: string[][] = [
  ['Jo Smith', 'Alex Jones', 'Helen Dean'],
  [],
  ['Sam Dale'],
  ['Helen Dean', 'Steven Bond'],
  ['Alex Jones', 'Jo Smith', 'Sam Dale', 'Helen Dean'],
  [],
];

function buildGeneratedProjects(): MockProjectSeed[] {
  const oldestBaseDate = new Date('2026-04-24T12:00:00Z');

  return Array.from({ length: GENERATED_PROJECT_COUNT }, (_, i) => {
    const lastModified = new Date(oldestBaseDate);
    lastModified.setUTCDate(lastModified.getUTCDate() - i);

    return {
      name: `${GENERATED_TOPICS[i % GENERATED_TOPICS.length]} ${2026 - Math.floor(i / GENERATED_TOPICS.length)}`,
      description: GENERATED_DESCRIPTIONS[i % GENERATED_DESCRIPTIONS.length],
      owner: GENERATED_OWNERS[i % GENERATED_OWNERS.length],
      team: GENERATED_TEAMS[i % GENERATED_TEAMS.length],
      visibility: GENERATED_VISIBILITIES[i % GENERATED_VISIBILITIES.length],
      sharedWith: GENERATED_SHARED_WITH[i % GENERATED_SHARED_WITH.length],
      numberOfFiles: ((i * 7) % 24) + 1,
      lastModifiedDatetime: lastModified.toISOString(),
    };
  });
}

/** Sam Dale is the signed-in user in the designs, so their projects go to whoever is signed in. */
const DESIGN_CURRENT_USER = 'Sam Dale';

const CURRENT_USER_OWNER_ID = 'mock-current-user';

const getMockOwnerId = (owner: string) =>
  owner === DESIGN_CURRENT_USER ? CURRENT_USER_OWNER_ID : `mock-user-${owner.toLowerCase().replace(/\W+/g, '-')}`;

export const MOCK_PROJECTS: ProjectData[] = [...BASE_PROJECTS, ...buildGeneratedProjects()].map((project, index) => ({
  ...project,
  projectId: `mock-project-${index + 1}`,
  ownerId: getMockOwnerId(project.owner),
}));

type UserAboutState = { userAbout?: { userAboutData?: UserAboutData | null } };

/** Gives Sam Dale's projects to the signed-in user, so they own some projects and not others. */
export function withMockCurrentUser(project: ProjectData, state: unknown): ProjectData {
  const currentUser = (state as UserAboutState).userAbout?.userAboutData;
  if (project.ownerId !== CURRENT_USER_OWNER_ID || !currentUser) return project;

  return { ...project, owner: currentUser.name, ownerId: currentUser.userId };
}

function compareProjects(a: ProjectData, b: ProjectData, sortBy: string): number {
  switch (sortBy) {
    case 'name':
    case 'description':
    case 'owner':
    case 'team':
    case 'visibility':
      return (a[sortBy] ?? '').localeCompare(b[sortBy] ?? '');
    case 'numberOfFiles':
      return a.numberOfFiles - b.numberOfFiles;
    default:
      return new Date(a.lastModifiedDatetime).getTime() - new Date(b.lastModifiedDatetime).getTime();
  }
}

const matchesAny = (values: readonly string[] | undefined, value: string | null) =>
  !values?.length || (value !== null && values.includes(value));

/** Applies search (name + description), filters, sorting and pagination the way the real endpoint will. */
export function queryMockProjects(
  {
    pageIndex,
    pageSize,
    sortBy = 'lastModifiedDatetime',
    sortOrder = 'desc',
    search,
    owners,
    teams,
    visibilities,
  }: GetProjectsRequest,
  state: unknown,
): GetProjectsResponse {
  let projects = MOCK_PROJECTS.map((project) => withMockCurrentUser(project, state)).filter(
    (project) =>
      matchesAny(owners, project.owner) &&
      matchesAny(teams, project.team) &&
      matchesAny(visibilities, project.visibility),
  );

  const searchTerm = search?.trim().toLowerCase();
  if (searchTerm) {
    projects = projects.filter(
      (project) =>
        project.name.toLowerCase().includes(searchTerm) ||
        (project.description ?? '').toLowerCase().includes(searchTerm),
    );
  }

  const sorted = [...projects].sort((a, b) => {
    const result = compareProjects(a, b, sortBy);
    return sortOrder === 'asc' ? result : -result;
  });

  const start = pageIndex * pageSize;

  return {
    projects: sorted.slice(start, start + pageSize),
    numberOfResults: sorted.length,
  };
}

const distinctSorted = (values: (string | null)[]) =>
  [...new Set(values.filter((value): value is string => !!value))].sort((a, b) => a.localeCompare(b));

/** Every owner and team on a project, for the list's column filters. */
export function getMockProjectFilterOptions(state: unknown): GetProjectFilterOptionsResponse {
  const projects = MOCK_PROJECTS.map((project) => withMockCurrentUser(project, state));

  return {
    owners: distinctSorted(projects.map((project) => project.owner)),
    teams: distinctSorted(projects.map((project) => project.team)),
  };
}

type CachedListQuery<T> = { select: () => (state: unknown) => { data?: T } };

/** Users and teams for the project forms, read from the RTK Query cache so a mock row looks real. */
const cachedListEndpoints = api.endpoints as unknown as Partial<{
  getActiveUsersForOrg: CachedListQuery<GetActiveUsersForOrgResponse>;
  getTeamsList: CachedListQuery<TeamsListResponse>;
}>;

/** Resolves ids to names; an id that matches nothing is shown as-is. */
const getNameLookups = (state: unknown) => {
  const users = cachedListEndpoints.getActiveUsersForOrg?.select()(state).data?.activeUsers ?? [];
  const teams = cachedListEndpoints.getTeamsList?.select()(state).data?.teams ?? [];

  return {
    getUserName: (userId: string) => users.find((user) => user.fletchUserId === userId)?.userName ?? userId,
    getTeamName: (teamId: string | null) =>
      teamId ? (teams.find((team) => String(team.teamId) === teamId)?.name ?? teamId) : null,
  };
};

let createdProjectCount = 0;

/** Adds a project the way the real endpoint will, so it shows up first in the default "Last Modified" order. */
export function addMockProject(request: CreateProjectRequest, state: unknown): CreateProjectResponse {
  const { getUserName, getTeamName } = getNameLookups(state);

  createdProjectCount += 1;
  const projectId = `mock-created-project-${createdProjectCount}`;

  MOCK_PROJECTS.push({
    projectId,
    name: request.name,
    description: request.description,
    owner: getUserName(request.ownerId),
    ownerId: request.ownerId,
    team: getTeamName(request.teamId),
    visibility: request.visibility,
    sharedWith: request.sharedWithUserIds.map(getUserName),
    numberOfFiles: 0,
    lastModifiedDatetime: new Date().toISOString(),
  });

  return { projectId };
}

const byProjectId = (projectId: string) => (project: ProjectData) => project.projectId === projectId;

export function findMockProject(projectId: string): ProjectData | undefined {
  return MOCK_PROJECTS.find(byProjectId(projectId));
}

export function updateMockProject({ projectId, ...changes }: UpdateProjectRequest, state: unknown): boolean {
  const { getUserName, getTeamName } = getNameLookups(state);

  return replaceMockItem(MOCK_PROJECTS, byProjectId(projectId), (project) => ({
    ...project,
    name: changes.name ?? project.name,
    description: changes.description !== undefined ? changes.description : project.description,
    owner: changes.ownerId !== undefined ? getUserName(changes.ownerId) : project.owner,
    ownerId: changes.ownerId ?? project.ownerId,
    team: changes.teamId !== undefined ? getTeamName(changes.teamId) : project.team,
    visibility: changes.visibility ?? project.visibility,
    sharedWith: changes.sharedWithUserIds?.map(getUserName) ?? project.sharedWith,
    lastModifiedDatetime: new Date().toISOString(),
  }));
}

/** Keeps the list's file count in step with uploads. */
export function addToMockProjectFileCount(projectId: string, count: number) {
  replaceMockItem(MOCK_PROJECTS, byProjectId(projectId), (project) => ({
    ...project,
    numberOfFiles: project.numberOfFiles + count,
    lastModifiedDatetime: new Date().toISOString(),
  }));
}

/** Returns false when the project does not exist. */
export function deleteMockProject(projectId: string): boolean {
  const index = MOCK_PROJECTS.findIndex(byProjectId(projectId));
  if (index === -1) return false;

  MOCK_PROJECTS.splice(index, 1);
  return true;
}
